import dayjs from 'dayjs';
import { useConfirmationDialog } from '@dashhudson/dashing-ui/confirmation-dialog';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { ref } from 'vue';
import { useDashboardsShareableLinksStore } from '@/stores/dashboards-shareable-links';
import { DASHBOARD_DRAWERS } from '@/app/dashboards/constants';
import { DashboardUserEventTracker } from '@/app/dashboards/mixpanel';

export function useShareableLinkMenu() {
  const { launchDrawer } = useDrawer();
  const { addToast } = useToast({ life: 5000 });
  const linkToDelete = ref(null);

  const dashboardsShareableLinksStore = useDashboardsShareableLinksStore();
  const mixpanelTracker = new DashboardUserEventTracker();

  async function editLink(linkData) {
    await launchDrawer({
      name: DASHBOARD_DRAWERS.EDIT_SHAREABLE_LINK,
      props: {
        shareableLink: linkData,
      },
    });
  }

  async function renewLinkFor30Days(linkData) {
    const validUntil = dayjs().add(30, 'days').toISOString();
    try {
      await dashboardsShareableLinksStore.updateShareableDashboardLink({
        shareableLinkId: linkData.id,
        validUntil,
        enabled: true,
      });
      addToast({
        message: 'Shareable Link renewed for 30 days.',
        severity: 'success',
      });
      mixpanelTracker.shareableLinkRenewed({
        shareableLinkId: linkData.id,
      });
    } catch (error) {
      addToast({
        message: 'There was an error renewing the Shareable Link.',
        severity: 'error',
      });
    }
  }

  async function deactivateLink(linkData) {
    try {
      await dashboardsShareableLinksStore.updateShareableDashboardLink({
        shareableLinkId: linkData.id,
        enabled: false,
      });
      addToast({
        message: 'Shareable Link deactivated.',
        severity: 'success',
      });
      mixpanelTracker.shareableLinkDeactivated({
        shareableLinkId: linkData.id,
      });
    } catch (error) {
      addToast({
        message: 'There was an error deactivating the Shareable Link.',
        severity: 'error',
      });
    }
  }

  const { showConfirmationDialog: deleteLinkDialog } = useConfirmationDialog({
    group: 'global',
    header: 'Delete Shareable Link?',
    message:
      'Anyone using this Shareable Link to access this Dashboard will immediately lose access.',
    acceptLabel: 'Delete Shareable Link',
    acceptSeverity: 'danger',
    onAccept: async () => {
      try {
        await dashboardsShareableLinksStore.deleteShareableDashboardLink({
          shareableLinkId: linkToDelete.value.id,
        });
        addToast({
          message: 'Shareable Link deleted.',
          severity: 'success',
        });
        mixpanelTracker.shareableLinkDeleted({
          shareableLinkId: linkToDelete.value.id,
        });
      } catch (error) {
        addToast({
          message: 'There was an error deleting the Shareable Link.',
          severity: 'error',
        });
      }
    },
  });

  async function deleteLink(linkData) {
    linkToDelete.value = linkData;
    await deleteLinkDialog();
  }

  function getMenuOptions(linkData) {
    return [
      {
        icon: 'refresh-ccw',
        label: linkData.enabled ? 'Renew for 30 days' : 'Enable for 30 days',
        command: async () => {
          await renewLinkFor30Days(linkData);
        },
      },
      {
        icon: 'edit',
        label: 'Edit',
        command: () => {
          editLink(linkData);
        },
      },
      {
        icon: 'link-external',
        label: 'Preview Shareable Link',
        command: () => {
          window.open(linkData.shareableLink, '_blank');
        },
      },
      linkData.enabled
        ? {
            icon: 'eye-off',
            label: 'Deactivate Shareable Link',
            class: 'dash-text-danger dash-fill-icon-danger',
            command: async () => {
              await deactivateLink(linkData);
            },
          }
        : null,
      {
        icon: 'delete',
        label: 'Delete Shareable Link',
        disabled: false,
        class: 'dash-text-danger dash-fill-icon-danger',
        command: async () => {
          await deleteLink(linkData);
        },
      },
    ].filter(Boolean);
  }

  return {
    getMenuOptions,
  };
}
